/**
 * What an agent wrote, drawn (D358): the Markdown it is, with the rich blocks `deriveBlocks` recognised in it — stats, a
 * table, a steps tracker, a file list — drawn as the workbench draws them, so a reply reads the same in a browser as on
 * the desktop. A plan's outcome report ("Outcome met ✓ — 1/1 checks passed" and its Check / Result / Evidence table) is
 * one of these: the table keeps its words whole at every width, and in a narrow pane each check is a card of its own.
 *
 * Copied from the workbench's webview-ui/src/components/facilitator/DerivedBlocks.tsx (52bc328e). The workbench draws a
 * spec with its generative UI renderer; the console has no such renderer, so the four shapes the pass makes (a stack of
 * stats, a table, steps, files) are drawn here directly, with the renderer's own markup and classes (liveBlocks.css).
 * A file list has no Open: the files are on the machine, not here. The organisation's `conversations.richContent`
 * switch is the workbench's to apply; the console always draws the blocks.
 */
import type { DerivedSegment, UiNode, UiStep } from '@aioe/shared';
import { deriveBlocks } from '@aioe/shared';
import { Check, Clock, File, Xmark } from '@gravity-ui/icons';
import { memo, useMemo } from 'react';

import { Markdown } from '../Markdown.tsx';
import { ArtStat, type ArtTone } from '../ui.tsx';
import './liveBlocks.css';

const baseName = (path: string) => path.slice(path.lastIndexOf('/') + 1);

function FileList({ files }: { files: Array<{ path: string; note?: string }> }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-px overflow-hidden rounded-xl border border-separator bg-surface/60 p-0" aria-label="Files">
      {files.map((f) => (
        <li key={f.path} className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
          <File className="size-3.5 shrink-0 text-muted" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="font-medium text-foreground">{baseName(f.path)}</span>
            {f.path.includes('/') ? <span className="ml-1.5 text-muted [overflow-wrap:anywhere]">{f.path.slice(0, f.path.lastIndexOf('/'))}</span> : null}
            {f.note ? <span className="block text-muted">{f.note}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

const STEP_WORDS: Record<UiStep['state'], string> = { done: 'Done', running: 'Running', failed: 'Failed', waiting: 'Waiting', skipped: 'Skipped', todo: 'Not started' };
const stepTitle = (s: UiStep) => `${s.label} · ${STEP_WORDS[s.state]}${s.hint ? ` · ${s.hint}` : ''}`;

function StepMark({ state }: { state: UiStep['state'] }) {
  if (state === 'done') return <Check className="size-3" />;
  if (state === 'failed') return <Xmark className="size-3" />;
  if (state === 'waiting') return <Clock className="size-3" />;
  return null;
}

/** Stages as a segmented bar, one segment each, and their names beneath (the workbench's StepTrack). */
export function StepTrack({ steps, label, tone }: { steps: UiStep[]; label?: string; tone: string }) {
  const finished = steps.filter((s) => s.state === 'done' || s.state === 'skipped').length;
  const current = steps.find((s) => s.state === 'failed') ?? steps.find((s) => s.state === 'running') ?? steps.find((s) => s.state === 'waiting');
  return (
    <div className="ui-steps" data-tone={tone} role="group" aria-label={label ?? 'Progress'}>
      <div className="ui-steps__head">
        {label ? <span className="ui-label">{label}</span> : null}
        <span className="ui-steps__count">
          {current ? `${STEP_WORDS[current.state]}: ${current.label} · ` : ''}
          {finished} of {steps.length}
        </span>
      </div>
      <div className="ui-steps__bar" aria-hidden="true">
        {steps.map((s, i) => (
          <span key={i} className="ui-steps__seg" data-state={s.state} title={stepTitle(s)} />
        ))}
      </div>
      <ol className="ui-steps__list">
        {steps.map((s, i) => (
          <li key={i} className="ui-steps__item" data-state={s.state} title={stepTitle(s)}>
            <span className="ui-steps__dot">
              <StepMark state={s.state} />
            </span>
            <span className="ui-steps__name">{s.label}</span>
            <span className="sr-only"> · {STEP_WORDS[s.state]}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

type TableNode = Extract<UiNode, { type: 'table' }>;
const cellText = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** A table: rows and columns while every column keeps its words whole, else a card per row (liveBlocks.css). */
function DerivedTable({ node, alt }: { node: TableNode; alt: string }) {
  const rows = node.rows ?? [];
  const cols = node.columns;
  const size = cols.length <= 2 ? '2' : cols.length === 3 ? '3' : 'many';
  return (
    <div className="ui-table-box" data-cols={size}>
      <div className="ui-table-wrap">
        <table className="ui-table" aria-label={alt}>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} scope="col">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c, j) => (
                  <td key={c.key} className={j === 0 ? 'ui-table__lead' : undefined}>
                    {cellText(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="ui-rows" aria-label={alt}>
        {rows.map((r, i) => (
          <li key={i} className="ui-rows__row">
            <span className="ui-rows__title">{cellText(r[cols[0]!.key]) || '—'}</span>
            {cols.slice(1).map((c) =>
              cellText(r[c.key]) ? (
                <span key={c.key} className="ui-rows__pair">
                  <span>{c.label}: </span>
                  <span>{cellText(r[c.key])}</span>
                </span>
              ) : null,
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const TONES: readonly string[] = ['teal', 'violet', 'amber', 'blue', 'rose', 'green', 'slate'];
const toneOf = (t: unknown): ArtTone => (typeof t === 'string' && TONES.includes(t) ? (t as ArtTone) : 'slate');

/** One node of a derived spec: only the four shapes `deriveBlocks` makes. */
function Node({ node, alt }: { node: UiNode; alt: string }) {
  if (node.type === 'table') return <DerivedTable node={node} alt={alt} />;
  if (node.type === 'steps') return <StepTrack steps={node.steps ?? []} label={node.label} tone={node.tone ?? 'teal'} />;
  if (node.type === 'stat') return <ArtStat tone={toneOf(node.tone)} label={node.label} value={String(node.value)} detail={node.hint} />;
  if (node.type === 'stack') {
    const stats = node.children.filter((c) => c.type === 'stat');
    if (stats.length === node.children.length)
      return (
        <div className="ui-stats" style={{ ['--ui-cols' as string]: Math.min(stats.length, 4) }}>
          {stats.map((c, i) => (
            <Node key={i} node={c} alt={alt} />
          ))}
        </div>
      );
    return (
      <div className="flex min-w-0 flex-col gap-2">
        {node.children.map((c, i) => (
          <Node key={i} node={c} alt={alt} />
        ))}
      </div>
    );
  }
  return null;
}

function Segment({ segment }: { segment: DerivedSegment }) {
  if (segment.kind === 'markdown') return <Markdown breaks text={segment.text} className="min-w-0" />;
  if (segment.kind === 'files') return <FileList files={segment.files} />;
  return (
    <div className="derived-block min-w-0" data-derived={segment.recogniser}>
      {segment.spec.title ? <span className="ui-label mb-1 block">{segment.spec.title}</span> : null}
      <Node node={segment.spec.root} alt={segment.spec.alt ?? ''} />
    </div>
  );
}

/** Markdown with the blocks drawn from it. Replaces a bare `<Markdown>` wherever an agent's words are shown in a live session. */
export const RichMarkdown = memo(function RichMarkdown({ text }: { text: string }) {
  const segments = useMemo(() => deriveBlocks(text), [text]);
  if (segments.length === 1 && segments[0]!.kind === 'markdown') return <Markdown breaks text={segments[0]!.text} className="min-w-0" />;
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {segments.map((s, i) => (
        <Segment key={i} segment={s} />
      ))}
    </div>
  );
});
